import { useState, useEffect, useRef } from "react";

interface FloatingShareBarProps {
  title: string;
  sourceUrl: string;
}

export default function FloatingShareBar({ title, sourceUrl }: FloatingShareBarProps) {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Apparaît après avoir scrollé 300px
  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Ferme le menu si on clique ailleurs
  useEffect(() => {
    if (!expanded) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest("[data-share-bar]")) setExpanded(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [expanded]);

  const shareWhatsApp = () => {
    window.open(
      `https://wa.me/?text=${encodeURIComponent(title + " - Wazi Stories Afrique\n" + sourceUrl)}`,
      "_blank"
    );
    setExpanded(false);
  };

  const shareLinkedIn = () => {
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(sourceUrl)}`,
      "_blank"
    );
    setExpanded(false);
  };

  const shareFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(sourceUrl)}`,
      "_blank"
    );
    setExpanded(false);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(sourceUrl);
      setCopied(true);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback silencieux
    }
    setExpanded(false);
  };

  return (
    <>
      {/* ── Barre flottante mobile (bottom center) ── */}
      <div
        data-share-bar
        className={`
          fixed bottom-6 left-1/2 -translate-x-1/2 z-50
          flex items-center gap-2
          transition-all duration-500 ease-out
          md:hidden
          ${visible ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-8 pointer-events-none"}
        `}
      >
        {/* Boutons qui s'ouvrent vers le haut */}
        <div
          className={`
            flex items-center gap-2
            transition-all duration-300 ease-out
            ${expanded ? "opacity-100 scale-100" : "opacity-0 scale-75 pointer-events-none"}
          `}
        >
          {/* WhatsApp */}
          <button
            onClick={shareWhatsApp}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-green-500 text-white shadow-lg hover:bg-green-600 active:scale-95 transition-all cursor-pointer"
            title="Partager sur WhatsApp"
          >
            <i className="ri-whatsapp-line text-xl"></i>
          </button>

          {/* LinkedIn */}
          <button
            onClick={shareLinkedIn}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-blue-600 text-white shadow-lg hover:bg-blue-700 active:scale-95 transition-all cursor-pointer"
            title="Partager sur LinkedIn"
          >
            <i className="ri-linkedin-line text-xl"></i>
          </button>

          {/* Facebook */}
          <button
            onClick={shareFacebook}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-blue-500 text-white shadow-lg hover:bg-blue-600 active:scale-95 transition-all cursor-pointer"
            title="Partager sur Facebook"
          >
            <i className="ri-facebook-line text-xl"></i>
          </button>

          {/* Copier le lien */}
          <button
            onClick={copyLink}
            className="w-12 h-12 flex items-center justify-center rounded-full bg-gray-700 text-white shadow-lg hover:bg-gray-800 active:scale-95 transition-all cursor-pointer"
            title="Copier le lien"
          >
            <i className={`text-xl ${copied ? "ri-check-line text-green-400" : "ri-link-m"}`}></i>
          </button>
        </div>

        {/* Bouton principal "Partager" */}
        <button
          onClick={() => setExpanded((v) => !v)}
          className={`
            flex items-center gap-2 px-5 h-12 rounded-full font-semibold text-sm shadow-xl
            transition-all duration-300 active:scale-95 cursor-pointer whitespace-nowrap
            ${expanded
              ? "bg-gray-800 text-white"
              : "bg-orange-500 text-white hover:bg-orange-600"
            }
          `}
        >
          <i className={`text-lg transition-transform duration-300 ${expanded ? "ri-close-line rotate-90" : "ri-share-line"}`}></i>
          {expanded ? "Fermer" : "Partager"}
        </button>
      </div>

      {/* ── Barre flottante desktop (côté droit) ── */}
      <div
        className={`
          fixed right-6 top-1/2 -translate-y-1/2 z-50
          hidden md:flex flex-col items-center gap-3
          transition-all duration-500 ease-out
          ${visible ? "opacity-100 translate-x-0 pointer-events-auto" : "opacity-100 translate-x-20 pointer-events-none"}
        `}
      >
        {/* Label */}
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest [writing-mode:vertical-rl] rotate-180 mb-1">
          Partager
        </span>

        {/* WhatsApp */}
        <button
          onClick={shareWhatsApp}
          title="Partager sur WhatsApp"
          className="group relative w-11 h-11 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-500 hover:bg-green-500 hover:text-white hover:border-green-500 transition-all duration-200 cursor-pointer shadow-sm"
        >
          <i className="ri-whatsapp-line text-lg"></i>
          <span className="absolute right-14 bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            WhatsApp
          </span>
        </button>

        {/* LinkedIn */}
        <button
          onClick={shareLinkedIn}
          title="Partager sur LinkedIn"
          className="group relative w-11 h-11 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-500 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all duration-200 cursor-pointer shadow-sm"
        >
          <i className="ri-linkedin-line text-lg"></i>
          <span className="absolute right-14 bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            LinkedIn
          </span>
        </button>

        {/* Facebook */}
        <button
          onClick={shareFacebook}
          title="Partager sur Facebook"
          className="group relative w-11 h-11 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-500 hover:bg-blue-500 hover:text-white hover:border-blue-500 transition-all duration-200 cursor-pointer shadow-sm"
        >
          <i className="ri-facebook-line text-lg"></i>
          <span className="absolute right-14 bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Facebook
          </span>
        </button>

        {/* Copier le lien */}
        <button
          onClick={copyLink}
          title="Copier le lien"
          className="group relative w-11 h-11 flex items-center justify-center rounded-full bg-white border border-gray-200 text-gray-500 hover:bg-gray-800 hover:text-white hover:border-gray-800 transition-all duration-200 cursor-pointer shadow-sm"
        >
          <i className={`text-lg transition-colors ${copied ? "ri-check-line text-green-500" : "ri-link-m"}`}></i>
          <span className="absolute right-14 bg-gray-900 text-white text-xs font-medium px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            {copied ? "Lien copié !" : "Copier le lien"}
          </span>
        </button>

        {/* Séparateur */}
        <div className="w-px h-6 bg-gray-200 rounded-full"></div>

        {/* WhatsApp prioritaire — bouton accent */}
        <button
          onClick={shareWhatsApp}
          className="w-11 h-11 flex items-center justify-center rounded-full bg-green-500 text-white hover:bg-green-600 transition-colors cursor-pointer shadow-md"
          title="Partager sur WhatsApp"
        >
          <i className="ri-whatsapp-line text-lg"></i>
        </button>
      </div>

      {/* Toast "Lien copié" */}
      <div
        className={`
          fixed bottom-24 left-1/2 -translate-x-1/2 z-50
          bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-full shadow-xl
          flex items-center gap-2
          transition-all duration-300
          md:hidden
          ${copied ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}
        `}
      >
        <i className="ri-check-line text-green-400"></i>
        Lien copié dans le presse-papier !
      </div>
    </>
  );
}
